import { compact, concat, isEmpty, keyBy, uniq } from 'lodash';
import type { TObjectTypesDetail } from 'types/state.interface';
import type { TType } from '@timeedit/types';
import type { TEIOption } from '@timeedit/ui-components';
import { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';

export const TEMP_ID = 0;

export type ObjectTypeWithFilter = Omit<TType, 'active'> & {
  key: number;
  title: string;
  active: string;
  parentIds?: number[];
  children?: ObjectTypeWithFilter[];
};

const convertDataTypesFilter = (
  ids: number[],
  allRecords: TType[],
  filterItem?: (type: TType) => boolean,
  parentIds?: number[]
): ObjectTypeWithFilter[] => {
  const result: ObjectTypeWithFilter[] = [];

  if (!ids) {
    return result;
  }

  for (const id of ids) {
    const type = allRecords.find((record) => record.id === id);

    if (!type) {
      continue;
    }

    const children = convertDataTypesFilter(
      type.subTypes,
      allRecords,
      filterItem,
      [...(parentIds || []), type.id]
    );

    const dataConverted = {
      ...type,
      key: type.id,
      title: type.name,
      active: String(type.active),
      parentIds,
      children: children,
    };

    if (!filterItem) {
      result.push(dataConverted);
    } else if (!isEmpty(children)) {
      // not a leaf node
      result.push(dataConverted);
    } else if (filterItem(type)) {
      // is leaf node (!children)
      result.push(dataConverted);
    }
  }

  return result;
};

type TFilterObjectTypes = {
  freeTextSearch: string;
  reservationTemplate: number;
  active: boolean;
};

export const filterObjectTypesItem = (
  filter: TFilterObjectTypes,
  type: TType
) => {
  const { freeTextSearch, reservationTemplate, active } = filter;
  if (reservationTemplate) {
    if (
      !type?.reservationTemplates ||
      !type.reservationTemplates.includes(reservationTemplate)
    ) {
      return false;
    }
  }
  if (
    freeTextSearch &&
    !(type.name || '').toLowerCase().includes(freeTextSearch.toLowerCase())
  ) {
    return false;
  }
  if (typeof active === 'boolean' && type.active !== active) {
    return false;
  }
  return true;
};

export const handleCombineObjectTypesWithFilter = (
  allTypes?: TType[],
  filterItem?: (item: TType) => boolean
) => {
  const dataConverted: ObjectTypeWithFilter[] = [];
  const listParentIds = new Set<number>([]);
  const listChildrenTypes = new Set<number>([]);

  for (const type of allTypes) {
    const { subTypes, active, id, name } = type;

    if (subTypes) {
      subTypes.map((i: number) => listChildrenTypes.add(i));
      listParentIds.add(id);
    }

    const children = convertDataTypesFilter(subTypes, allTypes, filterItem, [
      id,
    ]);

    const dataConvertedItem: ObjectTypeWithFilter = {
      ...type,
      key: id,
      title: name,
      active: String(active),
      children,
    };

    if (!filterItem || !isEmpty(children) || filterItem(type)) {
      dataConverted.push(dataConvertedItem);
    }
  }

  const dataFilter = dataConverted.filter(
    (item) => !listChildrenTypes.has(item.id)
  );
  return { dataFilter, listParentIds: Array.from(listParentIds) };
};

export const convertDataTypes2Form = (data: TObjectTypesDetail) => {
  if (!data) return {};

  return {
    name: data.name,
    fields: data.fields,
    description: data.description,
    extId: data.extId,
    id: data.id,
    active: data.active,
    personalData: data.personalData,
    sensitiveData: data.sensitiveData ?? false,
    parentFields: data.parentFields,
    history: data.history,
    altDesignations: data.altDesignations,
    allowStandardObjects: data.allowStandardObjects,
    allowAbstractObjects: data.allowAbstractObjects,
    allowVirtualObjects: data.allowVirtualObjects,
    personType: data.personType,
    locationType: data.locationType,
    aliasType: data.aliasType,
    externalOwner: data.externalOwner,
    organizations: data.organizations,
    documentation: data.documentation,
    parentIds: data.parentIds,
    reservationTemplates: data.reservationTemplates,
    advancedSettings: data.advancedSettings || [],
    skipLastReserved: data.skipLastReserved,
    objectCapacityFieldId: data.objectCapacityFieldId,
    objectSizeFieldId: data.objectSizeFieldId,
  };
};

export const isNewObjTypeId = (id: number) => id === TEMP_ID;

export type TTypeLocal = TType & {
  parentIds: number[];
};

export const createTypesLocal = (parent?: TTypeLocal) => {
  const parentIds = compact(concat(parent?.parentIds, parent?.id));
  const parentFields = compact(concat(parent?.parentFields, parent?.fields));

  return {
    name: '',
    fields: [],
    description: '',
    extId: null,
    id: TEMP_ID,
    isLocalData: true,
    active: true,
    personalData: false,
    sensitiveData: false,
    modified: '',
    modifiedBy: '',
    documentation: '',
    reservationTemplates: [],
    allowStandardObjects: true,
    allowAbstractObjects: false,
    allowVirtualObjects: false,
    personType: false,
    locationType: true,
    parentIds,
    aliasType: TEMP_ID,
    externalOwner: '',
    organizations: [],
    subTypes: [],
    parentFields,
    history: [],
    altDesignations: [],
    skipLastReserved: false,
  };
};

export const convertDataTypesForm2BodyUpdate = (data: any) => {
  return {
    name: data.name,
    fields: data.fields,
    description: data.description,
    extId: data.extId,
    active: data.active,
    personalData: data.personalData,
    sensitiveData: data.sensitiveData,

    allowStandardObjects: data.allowStandardObjects,
    allowAbstractObjects: data.allowAbstractObjects,
    allowVirtualObjects: data.allowVirtualObjects,
    altDesignations: data.altDesignations,
    personType: data.personType,
    locationType: data.locationType,
    aliasType: data.aliasType,
    externalOwner: data.externalOwner,
    organizations: data.organizations,
    documentation: data.documentation,
    reservationTemplates: data.reservationTemplates,
    skipLastReserved: data.skipLastReserved,
  };
};

export const convertDataTypesForm2BodyCreate = (data: any) => {
  return {
    name: data.name,
    fields: data.fields,
    description: data.description,
    extId: data.extId,
    active: data.active,
    personalData: data.personalData,
    sensitiveData: data.sensitiveData,

    allowStandardObjects: data.allowStandardObjects,
    allowAbstractObjects: data.allowAbstractObjects,
    allowVirtualObjects: data.allowVirtualObjects,
    personType: data.personType,
    locationType: data.locationType,
    aliasType: data.aliasType,
    externalOwner: data.externalOwner,
    organizations: data.organizations,
    reservationTemplates: data.reservationTemplates,
    skipLastReserved: data.skipLastReserved,
  };
};

export const ynOptions = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

export const getSubTypes = (typeId: number, typeTree: TTypeTree): number[] => {
  return [typeId, ...(typeTree[typeId]?.descendants || [])];
};

export type TTypeNode<ID extends string | number = number> = {
  id: ID;
  children: ID[];
  parents: ID[];
  ancestors: ID[];
  descendants: ID[];
};

export type TTypeTree<ID extends string | number = number> = Record<
  number,
  TTypeNode<ID>
>;

export const buildTypeTree = (types: TType[]): TTypeTree => {
  const typeTree: TTypeTree = {};

  for (const type of types) {
    const typeNode = addTypeNode(typeTree, type.id);
    typeNode.children = type.subTypes || [];

    for (const childId of typeNode.children) {
      const childNode = addTypeNode(typeTree, childId);

      childNode.parents.push(type.id);
      childNode.ancestors = uniq(
        concat(childNode.ancestors, type.id, typeNode.ancestors)
      );

      addDescendants(typeTree, childNode);
    }
  }

  return typeTree;
};

function addTypeNode(typeTree: TTypeTree, typeId: number) {
  const typeNode = typeTree[typeId] ?? {
    id: typeId,
    children: [],
    parents: [],
    ancestors: [],
    descendants: [],
  };

  typeTree[typeId] = typeNode;
  return typeNode;
}

function addDescendants(typeTree: TTypeTree, typeNode: TTypeNode) {
  typeNode.descendants = uniq(concat(typeNode.descendants, typeNode.children));

  for (const parentId of typeNode.parents) {
    const parentNode = typeTree[parentId];
    parentNode.descendants = uniq(
      concat(parentNode.descendants, typeNode.descendants)
    );
    addDescendants(typeTree, parentNode);
  }
}

export const createTypeOptions = (objectTypes: TType[]): TEIOption[] => {
  const objectTypeMap = keyBy(objectTypes, 'id');
  const checkedIds = new Set<number>([]);
  const convertToOptions = (type: TType): TEIOption | undefined => {
    const { id, name, subTypes: subTypeIds } = type;
    if (checkedIds.has(id)) {
      return undefined;
    }
    checkedIds.add(id);
    const subTypes: TType[] = [];
    subTypeIds?.forEach((subTypeId) => {
      const subType = objectTypeMap[subTypeId];
      if (subType) {
        subTypes.push(subType);
      }
    });
    return {
      value: id.toString(),
      label: name,
      children:
        subTypeIds && subTypeIds.length > 0
          ? compact(subTypes.map(convertToOptions))
          : undefined,
    };
  };
  return compact(objectTypes.map(convertToOptions));
};

/**
 * @param props.typesOrFields - An array of types or fields
 * @returns An array of default options for a select, with string labels
 * containing the name followed by the extId in parens, and a value containing
 * the type id. The array is sorted by the label.
 */
export function createSortedFieldOrTypeOptions<
  T extends { name: string; extId: string | number; id: number }[],
>(typesOrFields: T): DefaultOptionType[] {
  const options = typesOrFields.map((t) => ({
    label: `${t.name} (${t.extId})`,
    value: t.id.toString(),
  }));
  if (options.length > 0) {
    options.push({ label: '', value: '' });
  }
  options.sort((a, b) => a.label.localeCompare(b.label));
  return options;
}
